import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { useTranslation } from "react-i18next";

type Placement = "top" | "left";

interface InputInstructionsHoverProps {
  children: ReactNode;
  translationKey?: string;
  instruction?: string;
  className?: string;
}

const Wrapper = styled.div`
  position: relative;
  display: inline-flex;
  width: 100%;
`;

const HoverArea = styled.div<{ $placement: Placement }>`
  position: absolute;
  z-index: 1100;
  white-space: pre-wrap;

  ${({ $placement }) =>
    $placement === "top"
      ? css`
          left: 0;
          bottom: calc(100% + 8px);
        `
      : css`
          right: calc(100% + 8px);
          top: 50%;
          transform: translateY(-50%);
        `}
`;

const Tooltip = styled.div`
  max-width: 360px;
  min-width: 220px;
  padding: 10px 12px;
  border: 1px solid var(--sumi-border-color);
  border-radius: 8px;
  background: #fff;
  color: #333;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.16);
  font-size: 0.75rem;
  line-height: 1.4;

  a {
    color: var(--sumi-blue);
    text-decoration: underline;
  }
`;

const InstructionText = styled.span`
  white-space: pre-line;
`;

// Supports markdown-style links `[label](url)` and underline `__text__`
const markupRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|__([^_]+)__/g;

export const renderInstructionWithLinks = (instruction: string) => {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = markupRegex.exec(instruction)) !== null) {
    const fullMatch = match[0];
    const linkLabel = match[1];
    const linkUrl = match[2];
    const underlineText = match[3];
    const start = match.index;
    const end = start + fullMatch.length;

    if (start > lastIndex) {
      nodes.push(
        <React.Fragment key={`text-${lastIndex}`}>
          {instruction.slice(lastIndex, start)}
        </React.Fragment>,
      );
    }

    if (underlineText !== undefined) {
      nodes.push(<u key={`underline-${start}`}>{underlineText}</u>);
    } else {
      nodes.push(
        <a
          key={`link-${start}`}
          href={linkUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {linkLabel}
        </a>,
      );
    }

    lastIndex = end;
  }

  if (lastIndex < instruction.length) {
    nodes.push(
      <React.Fragment key={`text-${lastIndex}`}>
        {instruction.slice(lastIndex)}
      </React.Fragment>,
    );
  }

  return nodes;
};

export const InputInstructionsHover: React.FC<InputInstructionsHoverProps> = ({
  children,
  translationKey,
  instruction,
  className,
}) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [placement, setPlacement] = useState<Placement>("top");
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const closeTimeoutRef = useRef<number | null>(null);

  const resolvedInstruction = useMemo(() => {
    const normalizeLineBreaks = (value?: string) =>
      value ? value.replace(/\\n/g, "\n") : value;

    if (translationKey) {
      const translated = t(translationKey, { defaultValue: "" });
      return translated && translated !== translationKey
        ? normalizeLineBreaks(translated)
        : undefined;
    }

    return normalizeLineBreaks(instruction);
  }, [instruction, t, translationKey]);

  const hasInstruction = Boolean(resolvedInstruction?.trim());

  const instructionContent = useMemo(() => {
    if (!resolvedInstruction) return null;
    return renderInstructionWithLinks(resolvedInstruction);
  }, [resolvedInstruction]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current !== null) {
        window.clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!visible) return;

    const recalculatePlacement = () => {
      const wrapperRect = wrapperRef.current?.getBoundingClientRect();
      const tooltipRect = tooltipRef.current?.getBoundingClientRect();

      if (!wrapperRect || !tooltipRect) return;

      const topSpace = wrapperRect.top;
      const leftSpace = wrapperRect.left;
      const needsLeftPlacement =
        topSpace < tooltipRect.height + 12 &&
        leftSpace >= tooltipRect.width + 12;

      setPlacement(needsLeftPlacement ? "left" : "top");
    };

    const frame = window.requestAnimationFrame(recalculatePlacement);
    window.addEventListener("resize", recalculatePlacement);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", recalculatePlacement);
    };
  }, [visible]);

  const handleEnter = () => {
    if (!hasInstruction) return;

    if (closeTimeoutRef.current !== null) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }

    setVisible(true);
  };

  const handleLeave = () => {
    if (!hasInstruction) return;

    if (closeTimeoutRef.current !== null) {
      window.clearTimeout(closeTimeoutRef.current);
    }

    closeTimeoutRef.current = window.setTimeout(() => {
      setVisible(false);
      closeTimeoutRef.current = null;
    }, 120);
  };

  return (
    <Wrapper
      ref={wrapperRef}
      className={className}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onFocus={handleEnter}
      onBlur={handleLeave}
    >
      {children}
      {visible && hasInstruction ? (
        <HoverArea $placement={placement}>
          <Tooltip ref={tooltipRef} role="tooltip">
            <InstructionText>{instructionContent}</InstructionText>
          </Tooltip>
        </HoverArea>
      ) : null}
    </Wrapper>
  );
};
